// LG card variant sync — ONE `product:select` listener, THREE jobs (3rd added 2026-07-15):
//   (1) variant → cart-id sync (ADR-0016 shape 2; horizon-mechanics §1.11)
//   (2) CTA label swap "Add to Bag" ↔ "Sold Out" on unavailable in-card selection (BUG C)
//   (3) variant image swap — the card photo follows the in-card selection (§4.10)
//
// All three jobs ride the SAME Horizon signal — `StandardEvents.productSelect`, dispatched by
// `SwatchesVariantPickerComponent`, bubbling through the `<product-card>` host (which consumes
// it for the price/URL/availability morph, assets/product-card.js:205-241) and on to document.
// We delegate on `document` so morphed-in cards (infinite scroll / section re-render) are
// covered without re-registration. Zero vendor edits — we extend the listener, never patch
// Horizon (horizon-mechanics §4.2: skin/extend, don't compete).
//
// --- (1) cart-id sync ---------------------------------------------------------------------
// Closes the shape-2 gap where in-card variant switching morphed price / URL / CTA
// availability but left the add-to-bag form's hidden `name="id"` input at first_variant — so a
// shopper could select shade B, see shade B's price, and add shade A to cart. The id is taken
// from the SAME resolved payload the vendor price morph uses — `detail.resource` is the
// fragment's `{{ selected_variant | json }}` (sections/section-rendering-product-card.liquid,
// resolved in assets/variant-picker.js:385-394) — so the displayed price and the cart id can
// never disagree. On an unavailable combination the payload resolves variant:null/resource:null
// → we leave the input untouched; the vendor morph disables the CTA, so no stale-id add.
//
// --- (2) CTA label swap -------------------------------------------------------------------
// Selecting an UNAVAILABLE variant must change the add-to-bag CTA to "Sold Out" + inert, like a
// fully-OOS product — not merely leave it "Add to Bag". Horizon's
// `ProductCard.#isUnavailableVariantSelected` (assets/product-card.js:313-331) already sets
// `button.disabled`, but never changes the LABEL, and the CTA is NOT a morph target (the
// fragment's `.add-to-cart__button` is an empty hidden wrapper; only price / URL / swatch host
// are morphed). So there is no morph path for the label; we swap it here from the same resolved
// `detail.html` Horizon uses. Label strings come from data-label-available / data-label-soldout
// on the CTA (rendered by snippets/lg-product-card.liquid → Liquid-localized, no JS i18n).
//
// Scope note: only cards whose CTA is the interactive add-to-bag form participate. A
// product-level OOS card renders an already-inert "Sold Out" CTA in Liquid (WS-3) and freezes
// its switcher (pointer-events:none), so the in-card morph loop never fires there.
//
// --- (3) variant image swap ---------------------------------------------------------------
// The LG card is a single media tile (no Horizon slideshow), so every vendor image path
// no-ops: `#updateVariantImages` is slideshow-gated (product-card.js:336-355), `#setImageSrcset`
// is view-transition-gated, preview/reset are pointerenter-gated — the img is uncontested
// client territory, and the media zone is deliberately NOT a morph target (§1.20). We swap
// `.lg-product-card__img` src/srcset/alt from the SAME resolved payload as jobs 1-2:
// `detail.resource.featured_media` (theme-dev-pinned 2026-07-15: key ABSENT when the variant
// has no attached image — no product fallback; `preview_image.src` is protocol-relative
// `//host/...` with a `?v=` query; `alt` may be null). Attributes are mutated IN PLACE
// (element identity keeps the hover transform), URLs rebuilt via the URL API with the same
// width ladder Liquid emitted, and the incoming candidate is PRELOADED before commit (no
// blank flash; a per-img token drops stale decodes on rapid switching). A variant without
// media reverts to the Liquid-rendered baseline captured on first swap; when the resolved
// product id changes (combined-listing swap), the baseline re-anchors to the incoming image
// instead of reverting across products. Sold-out-but-existing variants resolve a non-null
// resource — the image swaps there too (job 2 flips the CTA in parallel). A facet/sort
// section morph resets src AND the baseline dataset together (morph.js preserves neither) —
// coherent reset, not a leak (§1.13).
//
// ADR-0249 extension (image overrides — job 3 precedence gate): `custom.image_overrides`
// metafields never ride `detail.resource` (`| json` skips metafields, §1.11), so the
// fragment precomputes the SELECTED variant's VARIANT-LEVEL override first image onto
// `<variant-picker data-lg-override-*>` (src/width/height/alt; src is a CDN URL the same
// `cdnImageUrl` resize applies to), and the Liquid card marks an override-driven baseline
// with `[data-lg-override]` on the <product-card> host. Gate, in order:
//   (a) payload override src → apply via the SAME preload-then-commit path;
//   (b) no payload src + host marker → revert to the Liquid baseline (which is already
//       override-first — product-level overrides apply to every variant, so native
//       featured_media must NOT be restored);
//   (c) neither → selected native featured_media, then native product media when
//       the initial card used a smallest-size variant MAIN (data-lg-variant-fallback).
// The new native fallback is used only for the matching product, never a combined-listing sibling.
// Baseline capture, combined-listing re-anchor, dedupe, decode-race token: all unchanged.

import { StandardEvents } from '@shopify/events';

// Mirror of the Liquid ladder in snippets/lg-product-card.liquid (image_url width 800 +
// widths '244, 292, 366, 488, 584, 732') — the `sizes` attribute is left untouched, so the
// browser's candidate selection math is unchanged after a swap.
const IMG_SRC_WIDTH = 800;
const IMG_SRCSET_WIDTHS = [244, 292, 366, 488, 584, 732];

/** Per-img token of the newest swap request — stale preloads check it before committing. */
const swapTokens = new WeakMap();

/**
 * Shopify CDN resize: set the `width` query param. URL API (never string-append) — the src
 * already carries `?v=…`, and the location.origin base absorbs protocol-relative `//host/…`.
 * @param {string} src
 * @param {number} width
 * @returns {string}
 */
function cdnImageUrl(src, width) {
  const url = new URL(src, location.origin);
  // The payload's src is protocol-relative (`//b04433.myshopify.com/…`, pinned 2026-07-15), so
  // it inherits the page protocol. On theme-dev (http://127.0.0.1) that yields an http CDN URL
  // and a per-image https redirect; Shopify hosts are https-only, so pin cross-origin hosts.
  if (url.origin !== location.origin) url.protocol = 'https:';
  url.searchParams.set('width', String(width));
  return url.toString();
}

/**
 * Captures the Liquid-rendered image attributes as the revert baseline (idempotent), keyed to
 * the product id so a combined-listing product swap can re-anchor instead of cross-reverting.
 * @param {HTMLImageElement} img
 * @param {string} productId
 */
function captureImageBaseline(img, productId) {
  if (img.dataset.lgBaseProductId != null) return;
  img.dataset.lgBaseProductId = productId;
  img.dataset.lgBaseSrc = img.getAttribute('src') ?? '';
  img.dataset.lgBaseSrcset = img.getAttribute('srcset') ?? '';
  img.dataset.lgBaseAlt = img.getAttribute('alt') ?? '';
  img.dataset.lgBaseWidth = img.getAttribute('width') ?? '';
  img.dataset.lgBaseHeight = img.getAttribute('height') ?? '';
}

/**
 * @param {HTMLImageElement} img
 * @param {{ src: string, srcset: string, alt: string, width: string, height: string }} attrs
 */
function applyImageAttributes(img, attrs) {
  // In-place attribute writes — never replace the element (class identity carries the
  // media-zone hover transform; there is no ref to re-wire).
  img.setAttribute('src', attrs.src);
  img.setAttribute('srcset', attrs.srcset);
  img.setAttribute('alt', attrs.alt);
  if (attrs.width) img.setAttribute('width', attrs.width);
  if (attrs.height) img.setAttribute('height', attrs.height);
}

/**
 * Reads the ADR-0249 override payload from a section-render response document — the
 * fragment precomputes the selected variant's VARIANT-LEVEL override first image onto
 * `<variant-picker data-lg-override-*>` (sections/section-rendering-product-card.liquid).
 * @param {Document} html
 * @returns {{ src: string, width: string, height: string, alt: string } | null}
 */
function readOverridePayload(html) {
  const picker = html.querySelector('variant-picker');
  if (!(picker instanceof HTMLElement)) return null;
  const src = picker.dataset.lgOverrideSrc;
  if (!src) return null;
  return {
    src,
    width: picker.dataset.lgOverrideWidth ?? '',
    height: picker.dataset.lgOverrideHeight ?? '',
    alt: picker.dataset.lgOverrideAlt ?? '',
  };
}

/**
 * Job 3 — swap the card image to the resolved variant's image (or revert).
 * @param {HTMLElement} card - the <product-card> host
 * @param {any} resource - the fragment's `{{ selected_variant | json }}` (non-null here)
 * @param {string} productId - detail.productId (already the NEW id on a combined-listing swap)
 * @param {{ src: string, width: string, height: string, alt: string } | null} override -
 *   ADR-0249 payload (variant-level override first image) from the fragment, or null
 */
function swapCardImage(card, resource, productId, override) {
  const img = /** @type {HTMLImageElement | null} */ (card.querySelector('.lg-product-card__img'));
  if (!img) return; // placeholder-SVG card (no product media at all) — never create an img

  // EVERY call supersedes any in-flight preload FIRST — the revert/dedupe/adopt early-returns
  // below must also kill a stale decode, or an older selection's image commits after a newer
  // one was finalized ("last selection wins" would break; review finding 2026-07-15).
  const token = {};
  swapTokens.set(img, token);

  captureImageBaseline(
    img,
    card.hasAttribute('data-lg-variant-fallback') ? (card.dataset.productId ?? productId) : productId
  );

  // ADR-0249 precedence gate (header block): (a) payload src → apply; (b) no payload src on
  // an override-baseline card ([data-lg-override] host marker) → src stays undefined and the
  // `!src` branch below reverts to the Liquid baseline — never native featured_media;
  // (c) neither → legacy featured_media path, unchanged.
  // Host data belongs only to its rendered product, never a combined-listing sibling.
  const sameProduct = card.dataset.productId === productId;
  const overrideBaseline = card.hasAttribute('data-lg-override');
  let media = null;
  if (!override && !overrideBaseline) {
    media = resource?.featured_media;
    if (!media?.preview_image?.src && sameProduct && card.hasAttribute('data-lg-variant-fallback')) {
      // A smallest-size initial image is not the product default. When the selected
      // size has no own image, use the original native product photo, not that MAIN.
      if (card.dataset.lgNativeSrc) {
        media = {
          id: card.dataset.lgNativeId,
          alt: card.dataset.lgNativeAlt,
          preview_image: {
            src: card.dataset.lgNativeSrc,
            width: card.dataset.lgNativeWidth,
            height: card.dataset.lgNativeHeight,
          },
        };
      }
    }
  }
  const src = override?.src ?? media?.preview_image?.src;

  // Combined-listing product swap: the stored baseline belongs to the OLD product — reverting
  // to it under the new product's price/URL would lie. Re-anchor the baseline to the INCOMING
  // image (the only image of the new product we know); when the incoming variant has no media,
  // best effort = keep what's shown and adopt it as the new baseline.
  const productChanged = img.dataset.lgBaseProductId !== productId;
  if (productChanged) {
    delete img.dataset.lgMediaId; // media ids are per-product; never dedupe across the swap
    if (!src) {
      // adopt the currently-shown attributes as the new product's baseline
      delete img.dataset.lgBaseProductId;
      captureImageBaseline(img, productId);
      return;
    }
    // fall through — the incoming attrs double as the new baseline, and the baseline's
    // product id moves WITH them inside the commit block (a superseded preload must not
    // leave the id pointing at the new product over the old product's attrs)
  }

  if (!src) {
    // Variant without an attached image (featured_media key absent — pinned 2026-07-15, no
    // product fallback in the payload) → revert to the Liquid-rendered product default.
    // No-op when nothing was swapped yet.
    if (img.dataset.lgMediaId == null) return;
    delete img.dataset.lgMediaId;
    applyImageAttributes(img, {
      src: img.dataset.lgBaseSrc ?? '',
      srcset: img.dataset.lgBaseSrcset ?? '',
      alt: img.dataset.lgBaseAlt ?? '',
      width: img.dataset.lgBaseWidth ?? '',
      height: img.dataset.lgBaseHeight ?? '',
    });
    return;
  }

  // Dedupe key: native media keep their per-product media id; override images have no media
  // id in the payload, so key on the version-stripped CDN path (URL pathname drops the `?v=`
  // cache-buster AND the width param — same identity rule as the Liquid resolver's merge
  // dedupe, ADR-0249).
  const mediaId = override
    ? `lg-override:${new URL(override.src, location.origin).pathname}`
    : String(media.id ?? '');
  if (mediaId && img.dataset.lgMediaId === mediaId) return; // same image (variants sharing media)

  const attrs = {
    src: cdnImageUrl(src, IMG_SRC_WIDTH),
    srcset: IMG_SRCSET_WIDTHS.map((w) => `${cdnImageUrl(src, w)} ${w}w`).join(', '),
    // alt fallback chain (a11y contract): native path byte-identical (`??` — an explicit
    // empty alt stays decorative). Override path uses `||`: Liquid serializes an unset
    // GLOBAL file alt (§5.20) as '' in the data attr, so '' means "unset" there and falls
    // back to the baseline alt (which bakes in the card's `| default: product.title`).
    alt: override ? override.alt || img.dataset.lgBaseAlt || '' : (media.alt ?? img.dataset.lgBaseAlt ?? ''),
    width: override ? override.width : media.preview_image.width ? String(media.preview_image.width) : '',
    height: override ? override.height : media.preview_image.height ? String(media.preview_image.height) : '',
  };

  // Preload the candidate the browser will most likely pick for this rendered size, then
  // commit — avoids a blank flash on slow networks. The hoisted token (top of function)
  // drops this decode when ANY later selection superseded it.
  const dpr = window.devicePixelRatio || 1;
  const targetWidth =
    IMG_SRCSET_WIDTHS.find((w) => w >= img.clientWidth * dpr) ?? IMG_SRC_WIDTH;
  const preloader = new Image();
  preloader.src = cdnImageUrl(src, targetWidth);
  preloader
    .decode()
    .catch(() => {}) // decode failure (e.g. CDN hiccup) → commit anyway; browser retries via srcset
    .then(() => {
      if (swapTokens.get(img) !== token) return; // superseded by a newer selection
      img.dataset.lgMediaId = mediaId;
      applyImageAttributes(img, attrs);
      if (productChanged) {
        // the incoming image doubles as the new product's revert baseline (id + attrs
        // re-anchor together, only on a COMMITTED swap)
        img.dataset.lgBaseProductId = productId;
        img.dataset.lgBaseSrc = attrs.src;
        img.dataset.lgBaseSrcset = attrs.srcset;
        img.dataset.lgBaseAlt = attrs.alt;
        img.dataset.lgBaseWidth = attrs.width;
        img.dataset.lgBaseHeight = attrs.height;
      }
    });
}

/**
 * Reads the resolved selected-variant availability from a section-render response.
 * @param {Document} html
 * @returns {boolean | null} true/false when a checked input is found, null when indeterminate.
 */
function selectedVariantAvailable(html) {
  const checked = html.querySelectorAll('input:checked');
  if (!checked.length) return null;
  // Last checked input is the resolved selection (matches product-card.js #toggleAddToCartButton
  // being called for each checked input, last value winning).
  const last = /** @type {HTMLInputElement} */ (checked[checked.length - 1]);
  return last.dataset.optionAvailable === 'true';
}

/**
 * Applies the add-to-bag / unavailable state to a card's CTA on in-card variant switch.
 *
 *   - available   → interactive "Add to Bag" (form submit).
 *   - unavailable → inert disabled CTA with the localized unavailable label ("AVAILABLE SOON").
 *
 * Only cards that shipped both labels participate (data-label-*). A fully-OOS card renders its
 * CTA in Liquid and freezes the switcher (pointer-events:none), so this never runs there.
 *
 * NOTE (2026-08-03): Notify-Me was pulled OFF the PLP card — the model is now exact Hydrogen
 * parity (PDP-only, one global toggle). The removed 3rd "notify" arm (data-lg-notify-open /
 * data-variant-id / data-product-title / --notify) lives in git history (feat/lg-notify-me) for
 * a fast re-add if PLP notify is ever reinstated.
 *
 * @param {HTMLElement} card - the <product-card> host
 * @param {boolean} available
 */
function applyCtaState(card, available) {
  const cta = /** @type {HTMLButtonElement | null} */ (card.querySelector('.lg-product-card__cta'));
  if (!cta) return;

  const soldOut = cta.dataset.labelSoldout;
  const addLabel = cta.dataset.labelAvailable;
  // Only cards that shipped both labels participate (the interactive add-to-bag CTA). A
  // Liquid-rendered OOS card has no data-label-available and is skipped.
  if (soldOut == null || addLabel == null) return;

  if (available) {
    cta.textContent = addLabel;
    cta.disabled = false;
    cta.removeAttribute('aria-disabled');
    cta.classList.remove('product-card__cta--disabled');
  } else {
    cta.textContent = soldOut;
    cta.disabled = true;
    cta.setAttribute('aria-disabled', 'true');
    cta.classList.add('product-card__cta--disabled');
  }
}

document.addEventListener(StandardEvents.productSelect, (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const card = target.closest('product-card');
  if (!(card instanceof HTMLElement)) return;

  const promise = /** @type {{ promise?: Promise<any> }} */ (event).promise;
  if (!(promise instanceof Promise)) return;

  promise
    .then((resolved) => {
      const { variant, detail } = resolved ?? {};

      // (1) cart-id sync — prefer the fragment's resolved resource id, fall back to variant.id.
      const idInput = card.querySelector('form.lg-product-card__form input[name="id"]');
      const id = detail?.resource?.id ?? variant?.id;
      if (idInput && id) idInput.value = String(id);

      // (2) CTA label swap — read availability from the resolved section-render document.
      const html = detail?.html;
      if (html instanceof Document) {
        const available = selectedVariantAvailable(html);
        if (available !== null) applyCtaState(card, available); // null = indeterminate; leave Horizon's disable untouched
      }

      // (3) image swap — needs the resolved variant payload. NOTE: a sold-out-but-existing
      // variant resolves a NON-null resource (available:false) and the image DOES swap (the
      // shopper sees the sold-out shade; job 2 flips the CTA in parallel). Null resource =
      // vendor skipped/couldn't resolve → leave the image alone, matching job 1.
      // ADR-0249: the override payload (variant-level custom.image_overrides first image)
      // rides the SAME fragment document — read it here so the gate inside swapCardImage
      // sees payload + marker + resource together.
      if (detail?.resource != null) {
        const override = html instanceof Document ? readOverridePayload(html) : null;
        swapCardImage(card, detail.resource, String(detail.productId ?? ''), override);
      }
    })
    .catch((error) => {
      // AbortError = superseded selection on rapid switching (vendor convention,
      // product-card.js:239) — the next select re-syncs; anything else is worth a warning.
      if (error?.name !== 'AbortError') console.warn('[lg-card-variant-sync]', error);
    });
});
